import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, FilePenLine, HandCoins, Wallet } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import {
  BudgetFormDialog,
  type BudgetFormSubmit,
} from '@/components/spending/budget-form-dialog';
import { ChartCard } from '@/components/shared/chart-card';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import {
  DateRangeFilter,
  EXTENDED_LIST_FILTER_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FilterBar } from '@/components/shared/filter-bar';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { StatCard } from '@/components/shared/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Listbox } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { PERMISSIONS, hasPermission, useCurrentMembership } from '@/lib/permissions';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatMoney, formatNumber } from '@/utils/format';

interface Budget {
  id: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  status: 'draft' | 'active' | 'closed';
  currency: string;
}

interface OffsetPage {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

interface BudgetInsights {
  draftCount: number;
  activeCount: number;
  closedCount: number;
  currency: string;
  plannedIncomeMinor: number;
  plannedExpenseMinor: number;
  byBudget: Array<{
    budgetId: string;
    name: string;
    status: Budget['status'];
    currency: string;
    plannedIncomeMinor: number;
    plannedExpenseMinor: number;
    plannedTotalMinor: number;
  }>;
}

const STATUS_ANY = '__any_status__';
const BUDGET_STATUSES = ['draft', 'active', 'closed'] as const;

const statusVariant = (status: Budget['status']) => {
  if (status === 'active') return 'success' as const;
  if (status === 'closed') return 'neutral' as const;
  return 'warning' as const;
};

export const BudgetsPage = () => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const timezone = usePreferences((state) => state.timezone);
  const canCreate = hasPermission(membership, PERMISSIONS.BUDGETS_CREATE);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(STATUS_ANY);
  const [filterDateRange, setFilterDateRange] = useState<DateRange>(() => ({
    preset: 'allTime',
    ...resolvePreset('allTime', timezone),
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [open, setOpen] = useState(false);

  const list = useQuery({
    queryKey: [
      'budgets',
      page,
      pageSize,
      search,
      filterStatus,
      filterDateRange.from,
      filterDateRange.to,
    ],
    queryFn: () =>
      api.get<{ data: Budget[]; page: OffsetPage }>('/budgets', {
        query: {
          page,
          pageSize,
          q: search.trim() || undefined,
          status: filterStatus === STATUS_ANY ? undefined : filterStatus,
          dateFrom: filterDateRange.from ?? undefined,
          dateTo: filterDateRange.to ?? undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });

  const insights = useQuery({
    queryKey: ['budgets', 'insights'],
    queryFn: () => api.get<BudgetInsights>('/budgets/insights'),
  });

  const appliedCount = useMemo(() => {
    let count = 0;
    if (search.trim()) count += 1;
    if (filterStatus !== STATUS_ANY) count += 1;
    if (filterDateRange.preset !== 'allTime') count += 1;
    return count;
  }, [filterDateRange.preset, filterStatus, search]);

  const create = useMutation({
    mutationFn: (values: BudgetFormSubmit) => api.post<Budget>('/budgets', values),
    onSuccess: (budget) => {
      toast.success(t('budgets.saved'));
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['budgets'] });
      void queryClient.invalidateQueries({ queryKey: ['budgets', 'insights'] });
      void navigate(`/budgets/${budget.id}`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Budget>[] = [
    {
      id: 'dates',
      header: t('budgets.columns.dates'),
      cell: (row) => `${formatDate(row.periodStart)} – ${formatDate(row.periodEnd)}`,
    },
    {
      id: 'name',
      header: t('budgets.columns.name'),
      cell: (row) => (
        <Link to={`/budgets/${row.id}`} className="font-medium text-fg hover:underline">
          {row.name}
        </Link>
      ),
    },
    {
      id: 'status',
      header: t('budgets.columns.status'),
      cell: (row) => (
        <Badge variant={statusVariant(row.status)}>{t(`budgets.status.${row.status}`)}</Badge>
      ),
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;

  return (
    <div>
      <PageHeader
        title={t('budgets.title')}
        description={t('budgets.description')}
        tipId="spending.expensesVsBudgets"
        headerActions={
          canCreate ? (
            <Button onClick={() => setOpen(true)}>{t('budgets.create')}</Button>
          ) : undefined
        }
      />

      <CollapsibleInsights
        summary={[
          {
            label: t('budgets.insights.active'),
            value: insights.data ? formatNumber(insights.data.activeCount) : '…',
          },
          {
            label: t('budgets.insights.drafts'),
            value: insights.data ? formatNumber(insights.data.draftCount) : '…',
          },
          {
            label: t('budgets.insights.plannedSpending'),
            value: insights.data
              ? formatMoney(insights.data.plannedExpenseMinor, insights.data.currency)
              : '…',
          },
        ]}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label={t('budgets.insights.active')}
            value={insights.data ? formatNumber(insights.data.activeCount) : undefined}
            icon={<CircleCheck className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('budgets.insights.drafts')}
            value={insights.data ? formatNumber(insights.data.draftCount) : undefined}
            icon={<FilePenLine className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('budgets.insights.plannedGiving')}
            money={
              insights.data
                ? {
                    amount: insights.data.plannedIncomeMinor,
                    currency: insights.data.currency,
                  }
                : undefined
            }
            icon={<HandCoins className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('budgets.insights.plannedSpending')}
            money={
              insights.data
                ? {
                    amount: insights.data.plannedExpenseMinor,
                    currency: insights.data.currency,
                  }
                : undefined
            }
            icon={<Wallet className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

        {(insights.data?.byBudget.length ?? 0) > 0 ? (
          <ChartCard title={t('budgets.insights.byBudget')} height={200}>
            <SimpleBarChart
              orientation="horizontal"
              data={(insights.data?.byBudget ?? []).map((row) => ({
                label: row.name,
                value: row.plannedTotalMinor,
              }))}
              formatValue={(value) =>
                formatMoney(value, insights.data?.currency ?? 'GHS', { showSymbol: true })
              }
              height={180}
            />
          </ChartCard>
        ) : null}
      </CollapsibleInsights>

      <FilterBar
        appliedCount={appliedCount}
        onClearAll={() => {
          setSearch('');
          setFilterStatus(STATUS_ANY);
          setFilterDateRange({
            preset: 'allTime',
            ...resolvePreset('allTime', timezone),
          });
          setPage(1);
        }}
        labels={{
          filters: t('budgets.filters.title'),
          clearAll: t('budgets.filters.clearAll'),
          applied: (count) => t('budgets.filters.applied', { count }),
        }}
        leading={
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('budgets.filters.search')}
            clearLabel={tCommon('search.clear')}
            className="max-w-none sm:w-56"
            aria-label={t('budgets.filters.search')}
          />
        }
      >
        {({ popoverPortal }) => (
          <>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={filterStatus}
                onChange={(value) => {
                  setFilterStatus(value ?? STATUS_ANY);
                  setPage(1);
                }}
                options={[
                  { value: STATUS_ANY, label: t('budgets.filters.statusAny') },
                  ...BUDGET_STATUSES.map((status) => ({
                    value: status,
                    label: t(`budgets.status.${status}`),
                  })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <DateRangeFilter
              value={filterDateRange}
              onChange={(range) => {
                setFilterDateRange(range);
                setPage(1);
              }}
              popoverPortal={popoverPortal}
              labels={{
                today: tCommon('date.today'),
                yesterday: tCommon('date.yesterday'),
                thisWeek: tCommon('date.thisWeek'),
                lastWeek: tCommon('date.lastWeek'),
                thisMonth: tCommon('date.thisMonth'),
                lastMonth: tCommon('date.lastMonth'),
                thisQuarter: tCommon('date.thisQuarter'),
                thisYear: tCommon('date.thisYear'),
                lastYear: tCommon('date.lastYear'),
                allTime: tCommon('date.allTime'),
                custom: tCommon('date.custom'),
                from: t('budgets.filters.dateFrom'),
                to: t('budgets.filters.dateTo'),
              }}
              presets={EXTENDED_LIST_FILTER_DATE_RANGE_PRESETS}
            />
          </>
        )}
      </FilterBar>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={
          <div className="space-y-3 text-center">
            <p className="text-body-sm text-fg-secondary">
              {appliedCount > 0 ? t('budgets.emptyFiltered') : t('budgets.empty')}
            </p>
            {canCreate && appliedCount === 0 ? (
              <Button onClick={() => setOpen(true)}>{t('budgets.create')}</Button>
            ) : null}
          </div>
        }
        onRowClick={(row) => void navigate(`/budgets/${row.id}`)}
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <BudgetFormDialog
        open={open}
        onOpenChange={setOpen}
        mode="create"
        submitting={create.isPending}
        onSubmit={(values) => create.mutate(values)}
      />
    </div>
  );
};
